import { OverlayModule } from '@angular/cdk/overlay';
import {
  Component,
  Signal,
  TemplateRef,
  computed,
  input,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { FullScreenDirective } from '../../../fullscreen/fullscreen.directive';
import { ToggleComponent } from '../../../toggle/toggle.component';
import type { GoogleMapsComponent } from '../../google-maps.component';
import { GoogleMapsControlType } from '../../model/google-maps-input.interface';
import { RouteSpeedControlComponent } from '../route-speed-control/route-speed-control.component';
import { ScaleControlComponent } from '../scale-control/scale-control.component';

/**
 * Declares `pozi-google-maps`' nine map-control templates and nothing else — it renders no DOM
 * of its own. It exists so the markup (45% of the parent's template) lives beside the styles
 * that dress it instead of inside a 300-line file.
 *
 * **It takes its parent as an input.** `ngTemplateOutlet` instantiates a `TemplateRef` in its
 * *declaration* context, so these templates read the parent's state wherever the parent chooses
 * to stamp them out. Passing the component itself keeps the markup a near-verbatim move and the
 * whole surface type-checked, at the cost of admitting that this child only works inside
 * `pozi-google-maps` — which is the truth. The parent is imported **type-only**, so there is no
 * runtime import cycle.
 *
 * Note the styles: the control markup carries *this* component's `_ngcontent` attribute even
 * though it is projected into the parent's `.controls` corners, so everything that dresses it
 * moved into this component's stylesheet with plain (not `:host`-prefixed) selectors.
 */
@Component({
  selector: 'pozi-map-control-templates',
  imports: [
    OverlayModule,
    RouterLink,
    FullScreenDirective,
    RouteSpeedControlComponent,
    ScaleControlComponent,
    ToggleComponent,
  ],
  templateUrl: './control-templates.component.html',
  styleUrl: './control-templates.component.scss',
})
export class ControlTemplatesComponent<TInfo = unknown> {
  /**
   * Generic only to follow the parent: an `InputSignal` is invariant in its value type (its
   * internal `transformFn` puts `TInfo` in a parameter position), so a non-generic
   * `input.required<GoogleMapsComponent>()` rejects `[host]="self"` from inside a
   * `GoogleMapsComponent<TInfo>`. Nothing in this child reads `TInfo`.
   */
  readonly host = input.required<GoogleMapsComponent<TInfo>>();

  private readonly mapTypeTemplate =
    viewChild<TemplateRef<unknown>>('mapTypeTemplate');
  private readonly layersTemplate =
    viewChild<TemplateRef<unknown>>('layersTemplate');
  private readonly fullScreenTemplate =
    viewChild<TemplateRef<unknown>>('fullScreenTemplate');
  private readonly zoomTemplate =
    viewChild<TemplateRef<unknown>>('zoomTemplate');
  private readonly routeSpeedTemplate =
    viewChild<TemplateRef<unknown>>('routeSpeedTemplate');
  private readonly scaleTemplate =
    viewChild<TemplateRef<unknown>>('scaleTemplate');
  private readonly titleTemplate =
    viewChild<TemplateRef<unknown>>('titleTemplate');
  private readonly routerLinkTemplate =
    viewChild<TemplateRef<unknown>>('routerLinkTemplate');
  private readonly minimizedTimelineTemplate = viewChild<TemplateRef<unknown>>(
    'minimizedTimelineTemplate'
  );

  /**
   * `null` until this component's own view exists — the parent consumes the record above the
   * element that declares it, so the queries are empty on the first pass. Every entry is
   * checked rather than assuming the nine arrive together, and the queries are deliberately
   * **not** `viewChild.required()`, which would throw NG0951 instead.
   */
  readonly templates: Signal<Record<
    GoogleMapsControlType,
    TemplateRef<unknown>
  > | null> = computed(() => {
    const entries: [GoogleMapsControlType, TemplateRef<unknown> | undefined][] =
      [
        ['mapType', this.mapTypeTemplate()],
        ['layers', this.layersTemplate()],
        ['fullScreen', this.fullScreenTemplate()],
        ['zoom', this.zoomTemplate()],
        ['routeSpeed', this.routeSpeedTemplate()],
        ['scale', this.scaleTemplate()],
        ['title', this.titleTemplate()],
        ['routerLink', this.routerLinkTemplate()],
        ['minimizedTimeline', this.minimizedTimelineTemplate()],
      ];

    return entries.every(([, template]) => !!template)
      ? (Object.fromEntries(entries) as Record<
          GoogleMapsControlType,
          TemplateRef<unknown>
        >)
      : null;
  });
}
